'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { addErrors, clearErrors } from '../../../store/user';
import React, { useState } from 'react';
import SublyApi from '../../../helpers/Api';
import { EMAIL_EXIST } from '../../../utils/constant/constants';
import SOISuccessModal from './SOISuccessModal';
import soiActionPlanPdf from './../../../assets/soi_action_plan_071825.pdf';
import Loader from '../../../utils/Loader';
import janeRotheLogo from '../../../assets/marketing/Jane Rothe/janeRotheLogoWhite.webp';
import { Link } from 'react-router-dom';

// Validation schema for firstName, lastName, and email
const formSchema = yup.object().shape({
	firstName: yup.string().required('First name is required'),
	lastName: yup.string().required('Last name is required'),
	email: yup.string().email('Invalid email address').required('Email is required'),
});

export default function SOILeadMagnet() {
	const dispatch = useDispatch();
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [loader, setLoader] = useState(false);

	// Functions to open and close the success modal
	const openSuccessModal = () => setIsSuccessModalOpen(true);
	const closeSuccessModal = () => setIsSuccessModalOpen(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm({
		mode: 'onChange',
		resolver: yupResolver(formSchema),
	});

	// Clear errors on mount and unmount
	React.useEffect(() => {
		dispatch(clearErrors());
		return () => {
			dispatch(clearErrors());
		};
	}, [dispatch]);

	const downloadFromAPI = async () => {
		try {
			// Fetch the PDF file as a blob
			const response = await fetch(soiActionPlanPdf);
			const blob = await response.blob();

			// Create a blob URL
			const blobUrl = URL.createObjectURL(blob);

			// Create and trigger download
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = 'Jane_Rothe_SOI_5_Day_Action_Plan.pdf';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Clean up the blob URL
			URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('Error downloading PDF:', error);
			// Fallback to original method
			const link = document.createElement('a');
			link.href = soiActionPlanPdf;
			link.download = 'Jane_Rothe_SOI_5_Day_Action_Plan.pdf';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const handleFormSubmit = async (data) => {
		setLoader(true);
		try {
			const response = await SublyApi.addNewContactToMailer({ data });
			setLoader(false);
			if (response?.bigmailer?.code === EMAIL_EXIST || response?.bigmailer?.id) {
				await downloadFromAPI();
				reset();
				openSuccessModal();
			}
		} catch (error) {
			setLoader(false);
			console.error('Error submitting form:', error);
			// Still show success modal and download even if API fails
			await downloadFromAPI();
			reset();
			openSuccessModal();
		}
	};

	return (
		<div className='min-h-screen bg-white'>
			{loader && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<Loader />
				</div>
			)}

			{/* Hero Section */}
			<div className='relative bg-clientBlack text-white py-20 px-6 md:px-12 lg:px-24'>
				{/* Logo - Upper Right Corner */}
				<div className='absolute top-6 right-6 md:top-8 md:right-8'>
					<Link to='/' className='block hover:opacity-80 transition-opacity'>
						<img
							src={janeRotheLogo}
							alt='Jane Rothe'
							className='h-8 md:h-10 lg:h-12 w-auto'
						/>
					</Link>
				</div>

				<div className='max-w-6xl mx-auto pt-16 md:pt-20 lg:pt-0'>
					<div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
						{/* Left: Headline & Form */}
						<div className='space-y-8'>
							<div className='space-y-4'>
								<h1 className='font-lejour text-4xl md:text-5xl lg:text-6xl leading-tight'>
									Turn your sphere into two deals this quarter—without awkward
									outreach
								</h1>
								<p className='font-montserrat-regular text-lg md:text-xl text-gray-300'>
									Grab the free, copy-and-paste 5-Day SOI Action Plan busy
									agents use in 10 minutes a day.
								</p>
							</div>

							{/* Form */}
							<form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-4'>
								<div>
									<input
										{...register('firstName')}
										type='text'
										id='firstName'
										placeholder='First name'
										className='w-full px-6 py-4 bg-white text-clientBlack font-montserrat-regular text-base border-2 border-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all'
										onKeyDown={(e) => {
											if (e.key === ' ' && e.target.value.length === 0) {
												e.preventDefault();
											}
										}}
									/>
									{errors.firstName && (
										<p className='mt-2 text-sm text-primary'>
											{errors.firstName.message}
										</p>
									)}
								</div>

								<div>
									<input
										{...register('lastName')}
										type='text'
										id='lastName'
										placeholder='Last name'
										className='w-full px-6 py-4 bg-white text-clientBlack font-montserrat-regular text-base border-2 border-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all'
										onKeyDown={(e) => {
											if (e.key === ' ' && e.target.value.length === 0) {
												e.preventDefault();
											}
										}}
									/>
									{errors.lastName && (
										<p className='mt-2 text-sm text-primary'>
											{errors.lastName.message}
										</p>
									)}
								</div>

								<div>
									<input
										{...register('email')}
										type='email'
										id='email'
										placeholder='Email address'
										className='w-full px-6 py-4 bg-white text-clientBlack font-montserrat-regular text-base border-2 border-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all'
										onKeyDown={(e) => {
											if (e.key === ' ') {
												e.preventDefault();
											}
										}}
									/>
									{errors.email && (
										<p className='mt-2 text-sm text-primary'>
											{errors.email.message}
										</p>
									)}
								</div>

								<button
									type='submit'
									className='w-full md:w-auto px-12 py-4 bg-primary text-white font-montserrat-medium text-lg uppercase hover:bg-opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl'>
									Get the Plan
								</button>
							</form>
						</div>

						{/* Right: Bullets - "What's inside" */}
						<div className='bg-secondaryBlack p-8 md:p-12 space-y-6'>
							<h2 className='font-montserrat-medium text-2xl md:text-3xl uppercase'>
								What's Inside
							</h2>
							<ul className='space-y-4 font-montserrat-regular text-base md:text-lg'>
								<li className='flex items-start'>
									<span className='text-primary mr-3 text-2xl'>•</span>
									<span>A systematic and achievable way to re-engage your SOI</span>
								</li>
								<li className='flex items-start'>
									<span className='text-primary mr-3 text-2xl'>•</span>
									<span>A plan that takes no more than 2–3 hours a day</span>
								</li>
								<li className='flex items-start'>
									<span className='text-primary mr-3 text-2xl'>•</span>
									<span>
										Simple enough to keep you consistent—even when you're busy
									</span>
								</li>
								<li className='flex items-start'>
									<span className='text-primary mr-3 text-2xl'>•</span>
									<span>Simple, repeatable pipeline-building activities</span>
								</li>
								<li className='flex items-start'>
									<span className='text-primary mr-3 text-2xl'>•</span>
									<span>
										Relationship-first: focus on people, not pushy asks
									</span>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* Testimonial Section */}
			<div className='bg-gray-50 py-20 px-6 md:px-12 lg:px-24'>
				<div className='max-w-4xl mx-auto text-center'>
					<div className='space-y-6'>
						<p className='font-montserrat-regular text-lg md:text-xl text-gray-700 leading-relaxed italic'>
							"My partner and I have been so impressed with the top of the line
							marketing we have received from Amy and her team at Jane Rothe.
							They really took the time to learn who we were and the message we
							wanted to convey to our clientele. They implemented a plan and
							strategy that has given our business so much consistency in our
							communication to our clients and elevated our brand to levels we
							didn't even foresee!"
						</p>
						<p className='font-montserrat-medium text-base text-gray-900'>
							— Elizabeth F.
						</p>
					</div>
				</div>
			</div>

			{/* Footer CTA */}
			<div className='bg-clientBlack text-white py-16 px-6 text-center'>
				<p className='font-montserrat-regular text-lg mb-4 text-gray-400'>
					Prefer hands-on help?
				</p>
				<a
					href='https://calendly.com/amy-janerothe/15-min-results-call'
					target='_blank'
					rel='noopener noreferrer'
					className='inline-block px-8 py-3 border-2 border-white text-white font-montserrat-medium text-base uppercase hover:bg-white hover:text-clientBlack transition-all duration-200'>
					Book a 15-Min Results Call
				</a>
			</div>

			{/* Success Modal */}
			{isSuccessModalOpen && (
				<SOISuccessModal
					isModalOpen={isSuccessModalOpen}
					closeModal={closeSuccessModal}
				/>
			)}
		</div>
	);
}
